import * as events from 'events';

import { AccessTokenChangedError, AccessTokenResult } from '~/Api/Authentication';
import ApiClient, { HttpVerb } from './ApiClient';
import { parseTemplate } from 'url-template';

import type { Store } from '~/Store';

const SLOW_NETWORK_FLASH_TIMEOUT = 5 * 1000;
const TIMEOUT_ERROR_MESSAGE = 'Error: Request timeout expired';

export class AuthenticatedUserChangedError extends AccessTokenChangedError {
	constructor(public readonly userEmail: string) {
		super();
	}
}

type AuthenticatedApiError = AuthenticatedUserChangedError;

export default class AuthenticatedApiClient {
	private readonly emitter: events.EventEmitter;

	constructor(private readonly getState: Store['getState'], private readonly apiClient: ApiClient) {
		this.emitter = new events.EventEmitter();
	}

	public userFetch(
		url: string,
		options: {
			body?: string;
			headers?: { [header: string]: string };
			method?: HttpVerb;
		} = {}
	): Promise<Response> {
		return this.checkTimeout(async () => {
			const { accessToken } = await this.obtainAccessToken();

			const { user } = accessToken;

			const joinedUrl = (
				parseTemplate('users/{userId}/').expand({
					userId: user.id,
				}) + url
			).replace('//', '/');

			return this.apiClient.fetch(joinedUrl, options);
		});
	}

	private checkTimeout<R>(createPromise: () => Promise<R>) {
		let slowMessageTimeoutId: NodeJS.Timeout | undefined;

		const timeoutPromise = new Promise((resolve) => {
			slowMessageTimeoutId = setTimeout(() => {
				resolve(new Error(TIMEOUT_ERROR_MESSAGE));
			}, SLOW_NETWORK_FLASH_TIMEOUT);
		});

		const actionPromise = createPromise().then((result) => {
			if (slowMessageTimeoutId) {
				clearTimeout(slowMessageTimeoutId);
			}

			return result;
		});

		Promise.race([actionPromise, timeoutPromise])
			.then((result) => {
				if (result instanceof Error && result.message === TIMEOUT_ERROR_MESSAGE) {
					this.handleTimeout(actionPromise);
					return Promise.reject(TIMEOUT_ERROR_MESSAGE);
				}
			})
			.catch((error) => {
				console.warn(error, actionPromise);
			});

		return actionPromise;
	}

	private async obtainAccessToken(): Promise<AccessTokenResult> {
		let { request } = this.getState().accessToken;
		if (request === null) {
			request = this.apiClient.fetchAccessToken();
		}

		try {
			const cachedResponse = await request;
			if (cachedResponse.accessToken.token === this.apiClient.loadAccessToken()) {
				return cachedResponse;
			}

			return this.apiClient.fetchAccessToken().then((response) => {
				const { user } = response.accessToken;
				if (user.id === cachedResponse.accessToken.user.id) {
					return response;
				}

				this.handleError(new AuthenticatedUserChangedError(user.email));
				return Promise.reject(new AuthenticatedUserChangedError(user.email));
			});
		} catch (e) {
			return this.apiClient.fetchAccessToken();
		}
	}

	private handleError(error: AuthenticatedApiError) {
		this.emitter.emit('error', error);
	}

	private handleTimeout(longPromise: Promise<any>) {
		this.emitter.emit('timeout', longPromise);
	}

	public addErrorListener(listener: (error: AuthenticatedApiError) => void) {
		this.emitter.addListener('error', listener);
	}

	public addTimeoutListener(listener: (proimse: Promise<any>) => void) {
		this.emitter.addListener('timeout', listener);
	}

	public destroy() {
		this.emitter.removeAllListeners();
	}
}
